import './MessageItem.scss';

import React, { PureComponent } from 'react';
import { faTrashAlt } from '@fortawesome/pro-duotone-svg-icons/faTrashAlt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Map } from 'immutable';
import PropTypes from 'prop-types';

import UpgradeWrapper from 'features/permissions/UpgradeWrapper';

import Avatar from 'components/Avatar';
import Box from 'components/Box';
import Button from 'components/clickables/Button';
import Icon from 'components/Icon';
import MessageBody from 'components/messenger/MessageBody';
import Text from 'components/Text';

import cxHelpers from 'util/className';
import { df, isToday, isYesterday, momentInZone } from 'util/dateTime';
import { toI18n } from 'util/i18n';
import { trackKinesisEvent, trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
} from 'util/tracking_constants';

@cxHelpers('MessageItem')
export default class MessageItem extends PureComponent {
  static propTypes = {
    message: PropTypes.instanceOf(Map).isRequired,
    onShowReadList: PropTypes.func,
    onDeleteMessage: PropTypes.func,
    currentUserCanDeleteMessages: PropTypes.bool,
  };

  getMDate = () =>
    momentInZone(this.props.message.get('created_at_sec'), 'unix');

  getDayTitle = () => {
    const mDate = this.getMDate();
    const dateMessage = mDate.format(df('month_day'));

    let primary = dateMessage;
    let secondary;

    if (isToday(mDate)) {
      primary = toI18n('time.today');
      secondary = dateMessage;
    } else if (isYesterday(mDate)) {
      primary = toI18n('time.yesterday');
      secondary = dateMessage;
    }

    return (
      <div className={this.cxEl('day-title')}>
        <strong>{primary}</strong>
        &nbsp;
        <span>{secondary}</span>
      </div>
    );
  };

  getDisplayTime = () => {
    const mDate = this.getMDate();
    const formatKey = isToday(mDate) ? 'msg_time' : 'msg_date_time';

    return mDate.format(df(formatKey));
  };

  getCanDeleteMessage = () => {
    const { message, onDeleteMessage, currentUserCanDeleteMessages } =
      this.props;
    return (
      !message.get('archived_at') &&
      onDeleteMessage &&
      (message.get('mine') || currentUserCanDeleteMessages)
    );
  };

  showReadStatus = () => {
    trackKinesisEvent('feature_usage', 'web_messenger_read_receipts');
    this.props.onShowReadList(this.props.message.get('received_messages'));
  };

  handleDeleteMessage = () => {
    trackUxEvent({
      productArea: PRODUCT_AREAS.MESSENGER,
      eventCategory: EVENT_CATEGORIES.MESSAGE,
      eventAction: EVENT_ACTIONS.DELETE_CLICKED,
    });
    this.props.onDeleteMessage(this.props.message);
  };

  renderMessageDetails() {
    const { message, onShowReadList } = this.props;
    const mine = message.get('mine');
    const archived = message.get('archived_at');
    return (
      <Box
        pt={10}
        inlineBlock
        vcenter
        className={this.cxEl('details', { mine })}
      >
        <Box vcenter pr={10}>
          <Text fs12>{this.detailsString()}</Text>
        </Box>
        <Box vcenter pr={3}>
          {!archived && onShowReadList && mine && this.renderReadCounter()}
        </Box>
      </Box>
    );
  }

  detailsString = () => {
    const details = [];
    const { message } = this.props;

    if (!message.get('mine')) {
      details.push(message.get('sending_user_name'));
    }

    details.push(this.getDisplayTime());

    return details.join(' | ');
  };

  renderReadCounter = () => {
    const { message } = this.props;
    const readCounter = message.get('readCounter');
    const participantsCounter = message.get('participantsCounter');

    return (
      <a className="read-counter" onClick={this.showReadStatus}>
        <UpgradeWrapper
          feature="messaging_read_receipts"
          focusedFeature="teamCommunicationAndManagement"
        >
          <Box bradius="6em" b="1px solid grayMediumLight" ph={10} pv={2}>
            <Text fs12 lh={1.5} fw400>
              <Icon
                className={this.cxEl('icon', 'read-count')}
                size="sm"
                color="blue"
                type="eye"
              />
              &nbsp;
              {readCounter}&nbsp;/&nbsp;{participantsCounter}
            </Text>
          </Box>
        </UpgradeWrapper>
      </a>
    );
  };

  renderMessageAvatar = () => (
    <Avatar url={this.props.message.get('sending_user_avatar')} />
  );

  renderDeleteButton = () => {
    if (this.getCanDeleteMessage()) {
      return (
        <Box className={this.cxEl('delete-link')}>
          <Button
            theme="link"
            icon="closeSimple"
            iconColor="grayDark"
            onClick={this.handleDeleteMessage}
          />
        </Box>
      );
    }
  };

  renderMessageBody = () => {
    const { message } = this.props;
    const mine = message.get('mine');
    const coverRequest = message.get('cover_request');

    return (
      <Box
        className={this.cxEl('body', {
          mine,
          card: coverRequest,
          document: message.get('type') === 'doc_link',
        })}
      >
        {message.get('visible_to_user_only') ? (
          <Box className={this.cxEl('visible-to-user-only')}>
            {toI18n('messenger.messages.only_visible_to_you')}
          </Box>
        ) : null}
        <MessageBody message={message} />
      </Box>
    );
  };

  renderTrashIcon = () => {
    const mine = this.props.message.get('mine');
    return (
      <Box inlineBlock className={this.cxEl('archived_icon', { mine })}>
        <FontAwesomeIcon icon={faTrashAlt} />
      </Box>
    );
  };

  renderDeletedMessage = () => {
    const { message } = this.props;
    const mine = message.get('mine');
    return (
      <Box className={this.cxEl('body', 'archived', { mine })}>
        {mine && this.renderTrashIcon()}
        {toI18n('messenger.deleted_message')}
        {!mine && this.renderTrashIcon()}
      </Box>
    );
  };

  render() {
    const { message } = this.props;
    const mine = message.get('mine');
    const body = message.get('body');
    const attachment = message.get('attachment');
    const coverRequest = message.get('cover_request');
    const showDetails = message.get('showDetails');
    const shoutout = message.get('shoutout');
    const coordinates = message.get('coordinates');
    const archived = message.get('archived_at');

    if (!body && !attachment && !coverRequest && !shoutout && !coordinates) {
      return null;
    }

    return (
      <div className={this.cx({ mine })}>
        {message.get('showDayTitle') && this.getDayTitle()}

        <Box vcenter className={this.cxEl('main', { mine })}>
          {mine ? this.renderDeleteButton() : this.renderMessageAvatar()}
          {archived ? this.renderDeletedMessage() : this.renderMessageBody()}
          {!mine && this.renderDeleteButton()}
        </Box>

        {mine || showDetails ? this.renderMessageDetails() : null}
      </div>
    );
  }
}
